/* eslint-disable sort-imports */
import { BulletinsService } from 'src/services/bulletins.service';
import DialogModal from 'src/components/ui/dialogModal';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { IAdminBulletinItem } from 'src/services/models/bulletins.service';
import { buildExpireBulletinPayload } from 'src/utils/bulletin-actions';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';

export type BulletinActionType = 'expire' | 'delete';

interface IPendingBulletinAction {
    type: BulletinActionType;
    item: IAdminBulletinItem;
}

export interface IBulletinActionResult {
    type: BulletinActionType;
    item: IAdminBulletinItem;
    message: string;
}

interface IUseBulletinActionsOptions {
    onSuccess: (result: IBulletinActionResult) => void;
    onError: (message: string) => void;
}

export interface IBulletinActionDialogProps {
    action: IPendingBulletinAction | null;
    isProcessing: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/** Confirmation flow shared by the bulletin list and the update page. */
export const useBulletinActions = ({
    onSuccess,
    onError,
}: IUseBulletinActionsOptions) => {
    const { t } = useTranslation();
    const [pendingAction, setPendingAction] =
        useState<IPendingBulletinAction | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const mountedRef = useRef(true);
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.ViewBulletinPage.${key}`, { defaultValue });

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        [],
    );

    const cancelAction = () => {
        if (!isProcessing) {
            setPendingAction(null);
        }
    };

    const confirmAction = async () => {
        if (!pendingAction || isProcessing) return;

        const { type, item } = pendingAction;
        const service = new BulletinsService();
        let succeeded = false;

        setIsProcessing(true);

        try {
            const response =
                type === 'delete'
                    ? await service.removeBulletin(item.bulletinId)
                    : await service.upsertBulletin(
                          buildExpireBulletinPayload(item),
                      );

            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error(
                    `Bulletin ${type} failed. Status: ${response.status}`,
                );
            }

            succeeded = true;
        } catch (error) {
            console.error(`Unable to ${type} the bulletin`, error);
        }

        if (!mountedRef.current) return;

        setIsProcessing(false);
        setPendingAction(null);

        if (succeeded) {
            onSuccess({
                type,
                item,
                message:
                    type === 'delete'
                        ? text(
                              'DeleteSuccess',
                              'Bulletin is successfully deleted.',
                          )
                        : text(
                              'ExpireSuccess',
                              'Bulletin is successfully updated to expire.',
                          ),
            });
        } else {
            onError(
                type === 'delete'
                    ? text(
                          'DeleteError',
                          'Unable to delete the bulletin. Please try again later.',
                      )
                    : text(
                          'ExpireError',
                          'Unable to expire the bulletin. Please try again later.',
                      ),
            );
        }
    };

    return {
        requestExpire: (item: IAdminBulletinItem) =>
            setPendingAction({ type: 'expire', item }),
        requestDelete: (item: IAdminBulletinItem) =>
            setPendingAction({ type: 'delete', item }),
        dialogProps: {
            action: pendingAction,
            isProcessing,
            onCancel: cancelAction,
            onConfirm: () => void confirmAction(),
        } as IBulletinActionDialogProps,
    };
};

export const BulletinActionDialog: React.FC<IBulletinActionDialogProps> = ({
    action,
    isProcessing,
    onCancel,
    onConfirm,
}) => {
    const { t } = useTranslation();
    // Keeps the texts while the dialog is closing.
    const lastTypeRef = useRef<BulletinActionType>('delete');
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.ViewBulletinPage.${key}`, { defaultValue });

    if (action) {
        lastTypeRef.current = action.type;
    }

    const isExpire = lastTypeRef.current === 'expire';

    return (
        <DialogModal
            titleContent={
                isExpire
                    ? text(
                          'ExpireModalTitle',
                          'Are you sure you want to expire this bulletin?',
                      )
                    : text(
                          'DeleteModalTitle',
                          'Are you sure you want to delete this bulletin?',
                      )
            }
            bodyContent={
                isExpire
                    ? text(
                          'ExpireModalBody',
                          'This action will change the bulletin’s end date to today’s date, effectively expiring the bulletin. Are you sure you want to proceed?',
                      )
                    : text(
                          'DeleteModalBody',
                          'This action is permanent and cannot be undone.',
                      )
            }
            open={!!action}
            cancelBtnLabel={text('ModalCancel', 'Cancel')}
            confirmBtnLabel={
                isExpire
                    ? text('ExpireModalConfirm', 'Yes, expire')
                    : text('DeleteModalConfirm', 'Yes, delete')
            }
            onCancelHandler={onCancel}
            onConfirmHandler={onConfirm}
            isLoading={isProcessing}
            keepOpenOnConfirm
        />
    );
};
